import type { TemplateFormField, TemplateFormFieldUpdate } from '@common/types/templateFormFields';
import { getIntakeFormTitle } from '@common/util';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    type SxProps,
    TextField,
    type Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AlertDialog from '@/components/AlertDialog';
import CommonTab from '@/components/CommonTab';
import BuilderFormComponent from '@/components/FormComponent/BuilderFormComponent';
import FullScreenLoader from '@/components/FullScreenLoader';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import LawFirmLogo from '@/components/LawFirmLogo';
import NewQuestionsDrawer from '@/components/NewQuestionsDrawer/NewQuestionsDrawer';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import {
    deleteFormField,
    updateFormFields,
    updateOrder,
    updateTemplateForm,
} from '@/services/TemplateFormsService';
import useQuoteIntakeSetupStore from '@/stores/useQuoteIntakeSetupStore';
import { findConditionalQuestions, getLastQuestionOrder, prepareGroupTemplate } from '@/utils';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

export default function IntakeDesign() {
    const theme = useTheme();
    const [showTip, setShowTip] = useState<boolean>(true);
    const [
        formData,
        activeQuote,
        setFormData,
        fetchQuoteTemplateDataOnEveryStep,
        updateStep4IntakeAfterReorder,
    ] = useQuoteIntakeSetupStore((state) => [
        state.formData,
        state.activeQuote,
        state.setFormData,
        state.fetchQuoteTemplateDataOnEveryStep,
        state.updateStep4IntakeAfterReorder,
    ]);
    const { quoteIntakeId } = useParams();
    const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
    const [questionDrawer, setQuestionDrawer] = useState<{
        isOpen: boolean;
        tab: string;
        formField: TemplateFormField | undefined;
    }>({ isOpen: false, tab: '', formField: undefined });
    const [questionDrawerLoading, setQuestionDrawerLoading] = useState<boolean>(false);
    const [deleteAlert, setDeleteAlert] = useState<{
        isOpen: boolean;
        field: TemplateFormField | undefined;
    }>({ isOpen: false, field: undefined });
    const [isDeleteFieldLoading, setIsDeleteFieldLoading] = useState<boolean>(false);
    const { activeLawFirmId } = useLawFirmContext();
    const lawFirmId = activeQuote.lawFirmId ?? activeLawFirmId;
    const isDisabled =
        formData?.step0?.intakeFormAdded === 'Reference' &&
        formData?.step0?.referencedIntakeOnlyQuoteTemplateId;

    const isCopy = formData?.step0.intakeFormAdded === 'Copy';

    const [dialogOpen, setDialogOpen] = useState(true);

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const intakeDesignContainerStyle: SxProps<Theme> = {
        display: 'flex',
        gap: '16px',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 168px)',
        overflow: 'auto',
    };

    const intakeDesignSectionStyle: SxProps<Theme> = {
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        height: 'fit-content',
        marginX: '32px',
    };

    const intakeDesignTextFieldStyle = {
        marginTop: '16px',
        width: '100%',
        fontSize: '14px',
    };

    const intakeDesignTextAreaStyles: SxProps<Theme> = {
        ...intakeDesignTextFieldStyle,
        '& .MuiOutlinedInput-root': {
            height: 'auto',
            padding: '8px 12px',
        },
    };

    const onEditInitHandler = (formField: TemplateFormField) => {
        setQuestionDrawer((prevState) => {
            return {
                ...prevState,
                formField: formField,
                isOpen: true,
            };
        });
    };

    // Methods
    // Updates the visibility of the question in the store
    const setVisibility = async (visible: boolean, item: TemplateFormField) => {
        // Use the produce function to create a draft of the formData
        const itemIndex = formData.step4.templateFormFields?.indexOf(item);
        const updatedStep4 = produce(formData, (draftFormData) => {
            // Make sure to create a copy of currentStep4
            const currentStep4 = { ...draftFormData.step4 };

            // Update the templateFormFields array
            currentStep4.templateFormFields = currentStep4.templateFormFields?.map((field) => {
                if (
                    item.questionSchema.name === field.questionSchema.name ||
                    ((item.questionSchema.name === 'firstIdType' ||
                        item.questionSchema.name === 'firstId') &&
                        (field.questionSchema.name === 'firstIdType' ||
                            field.questionSchema.name === 'firstId')) ||
                    ((item.questionSchema.name === 'secondIdType' ||
                        item.questionSchema.name === 'secondId') &&
                        (field.questionSchema.name === 'secondIdType' ||
                            field.questionSchema.name === 'secondId'))
                ) {
                    return { ...field, isVisibleToUser: visible };
                } else {
                    return field;
                }
            });

            // Update the step4 property in the draft
            draftFormData.step4 = currentStep4;
        });

        // Update the formData with the updated draft
        setFormData('step4', updatedStep4.step4);

        try {
            if (item.templateFormFieldId && itemIndex !== undefined && itemIndex !== -1) {
                await shouldCallPutApi(itemIndex, updatedStep4);
                successSnackbar('Form field updated successfully');

                // Check if the clicked item corresponds to a type
                const associatedTypes = {
                    firstId: 'firstIdType',
                    firstIdType: 'firstId',
                    secondId: 'secondIdType',
                    secondIdType: 'secondId',
                };

                if (item.questionSchema.name in associatedTypes) {
                    const associatedTypeName = associatedTypes[item.questionSchema.name];
                    const associatedTypeItem = updatedStep4.step4.templateFormFields.find(
                        (field) => field.questionSchema.name === associatedTypeName,
                    );
                    if (associatedTypeItem) {
                        const associatedTypeIndex =
                            updatedStep4.step4.templateFormFields.indexOf(associatedTypeItem);
                        await shouldCallPutApi(associatedTypeIndex, updatedStep4);
                    }
                }
            }
        } catch (error: any) {
            errorSnackbar(error?.response?.data || 'Something went wrong');
        }
    };

    // Updates the order of the question in the store
    const setNewOrder = async (
        order: {
            originalOrder: number;
            newOrder: number;
        },
        field: TemplateFormField,
    ): Promise<void> => {
        // prepare the payload and be ready to call the API
        // required ids to make the call quoteTemplateId, templateFormId and templateFormFieldId
        const payload = {
            preOrder: order.originalOrder,
            newOrder: order.newOrder,
        };

        const quoteTemplateId =
            activeQuote?.quoteTemplateId || formData.step4.apiResponse?.quoteTemplateId;
        const templateFormId = formData.step4.apiResponse?.templateFormId;
        const templateFormFieldId = field?.templateFormFieldId;

        // API Operations
        if (quoteTemplateId && templateFormId && templateFormFieldId) {
            try {
                // call the api to update the order of the field
                const updateOrderResp = await updateOrder(
                    quoteTemplateId,
                    templateFormId,
                    templateFormFieldId,
                    payload,
                );
                if (updateOrderResp) {
                    updateStep4IntakeAfterReorder(updateOrderResp);
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    // Updates the value of the header and footer descriptions in the store
    const handleValueChange = (key: string, value: string | File | undefined): void => {
        const updatedStep4 = produce(formData, (draftFormData) => {
            // Make sure to create a copy of currentStep4
            const currentStep4 = { ...draftFormData.step4 };
            currentStep4[key] = value;

            draftFormData.step4 = currentStep4;
        });

        setFormData('step4', updatedStep4.step4);
    };

    // Call Intake design fetch api - GET API
    const fetchData = async () => {
        if ((quoteIntakeId || activeQuote?.quoteTemplateId) && !formData.step4.apiResponse) {
            setIsFullScreenLoading(true);
            try {
                if (formData.step0.isIntakeCreated) {
                    await fetchQuoteTemplateDataOnEveryStep(
                        'step4',
                        quoteIntakeId || activeQuote?.quoteTemplateId,
                        '2',
                    );
                } else {
                    await fetchQuoteTemplateDataOnEveryStep(
                        'step4',
                        quoteIntakeId || activeQuote?.quoteTemplateId,
                    );
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsFullScreenLoading(false);
            }
        }
    };

    // Determine if the PUT API should be called
    const shouldCallPutApi = async (formGroupIndex: number, formData: any): Promise<void> => {
        const templateId = quoteIntakeId || activeQuote.quoteTemplateId;
        if (templateId) {
            // call the put API with the following params quoteTemplateId, templateFormId, templateFormFieldId
            // get templateFormId and templateFormFieldId
            const templateFormId = formData.step4.templateFormId;
            const templateFormFieldId =
                formData.step4.templateFormFields[formGroupIndex].templateFormFieldId;

            // call the API - set the response in the store and handle the loading state
            const payload = {
                title: formData.step4.templateFormFields[formGroupIndex].title,
                subTitle:
                    formData.step4.templateFormFields[formGroupIndex].subTitle !== ''
                        ? formData.step4.templateFormFields[formGroupIndex].subTitle
                        : 'NA',
                name:
                    formData.step4.templateFormFields[formGroupIndex].name ||
                    formData.step4.templateFormFields[formGroupIndex].questionSchema.name,
                questionSchema: formData.step4.templateFormFields[formGroupIndex].questionSchema,
                questionValidationSchema:
                    formData.step4.templateFormFields[formGroupIndex].questionValidationSchema,
                displayOrder: formData.step4.templateFormFields[formGroupIndex].displayOrder,
                isVisibleToUser: formData.step4.templateFormFields[formGroupIndex].isVisibleToUser,
                formGroupTitle: formData.step4.templateFormFields[formGroupIndex].formGroupTitle,
                isLocked: formData.step4.templateFormFields[formGroupIndex].isLocked,
            };

            if (templateFormId && templateFormFieldId) {
                try {
                    await updateFormFields(
                        templateId,
                        templateFormId as string,
                        templateFormFieldId as string,
                        payload,
                    );
                } catch (error) {
                    console.error(error);
                    throw error;
                }
            }
        }
    };

    // Lifecycle hooks
    useEffect(() => {
        fetchData();
    }, [quoteIntakeId]);

    const [groupedFields, setGroupedFields] = useState<any>({});
    useEffect(() => {
        // Don't run if there are no fields
        if (!formData?.step4?.templateFormFields?.length) {
            return;
        }
        setGroupedFields(
            prepareGroupTemplate(
                formData.step4.templateFormFields,
                formData.step0?.transactionType,
            ),
        );
    }, [formData?.step4?.templateFormFields]);

    const formTitle = getIntakeFormTitle(formData.step0?.transactionType);

    // Add new fields
    const handleConfirmAddNewField = async (
        fieldData: TemplateFormFieldUpdate | TemplateFormFieldUpdate[],
    ) => {
        if (questionDrawer.formField && !Array.isArray(fieldData)) {
            //Check that the question is empty or not
            if (!fieldData.title.trim() || fieldData.title.trim() === '') {
                errorSnackbar('Question cannot be empty!');
                return;
            }

            //Check that the question need to be added is already present or not
            const similarQuestion = formData.step4.templateFormFields?.find((que) => {
                const questionSchema = fieldData.questionSchema;
                if (
                    questionSchema.conditionValue !== undefined &&
                    que.questionSchema.conditionValue !== undefined
                ) {
                    return (
                        que.title === fieldData.title &&
                        que.name !== fieldData.name &&
                        que.questionSchema.conditionValue === questionSchema.conditionValue
                    );
                } else {
                    return que.title === fieldData.title && que.name !== fieldData.name;
                }
            });
            // Return from the function with an error
            if (similarQuestion) {
                errorSnackbar('This question already exists!');
                return;
            }
            //Edit mode
            const templateId = activeQuote.quoteTemplateId;
            const templateFormId = formData.step4.templateFormId;
            if (templateId && templateFormId && fieldData.templateFormFieldId) {
                setQuestionDrawerLoading(true);
                try {
                    await updateFormFields(
                        templateId,
                        templateFormId,
                        fieldData.templateFormFieldId,
                        {
                            ...fieldData,
                            subTitle: fieldData.subTitle ? fieldData.subTitle : 'NA',
                        },
                    );
                    const updatedFormData = produce(formData, (draftFormData) => {
                        const currentStep4 = { ...draftFormData.step4 };
                        currentStep4.templateFormFields = currentStep4.templateFormFields?.map(
                            (field) => {
                                if (fieldData.name === field.name) {
                                    return {
                                        ...field,
                                        ...fieldData,
                                    };
                                } else {
                                    return field;
                                }
                            },
                        );
                        draftFormData.step4 = currentStep4;
                    });
                    setFormData('step4', updatedFormData.step4);
                    setQuestionDrawer({ isOpen: false, tab: '', formField: undefined });
                    successSnackbar('Form field updated successfully');
                    setQuestionDrawerLoading(false);
                } catch (error) {
                    setQuestionDrawerLoading(false);
                    console.error(error);
                }
            }
            return;
        }
        if (!Array.isArray(fieldData)) {
            return;
        }
        const newFields = [...fieldData];
        //Adding new question
        //Check that the question need to be added is already present or not
        const similarQuestion =
            newFields.length === 1
                ? formData.step4.templateFormFields?.find((que) => {
                      if (
                          newFields[0]?.questionSchema.conditionValue !== undefined &&
                          que.questionSchema.conditionValue !== undefined
                      ) {
                          return (
                              que.title === newFields[0]?.title &&
                              que.name !== newFields[0]?.name &&
                              que.questionSchema.conditionValue ===
                                  newFields[0]?.questionSchema.conditionValue
                          );
                      } else {
                          return (
                              que.title === newFields[0]?.title && que.name !== newFields[0]?.name
                          );
                      }
                  })
                : formData.step4.templateFormFields.find((que) => {
                        if (
                            newFields[0]?.questionSchema.conditionValue !== undefined &&
                            que.questionSchema.conditionValue !== undefined
                        ) {
                            return (
                                que.title === newFields[0]?.title &&
                                que.name !== newFields[0]?.name &&
                                que.questionSchema.conditionValue ===
                                    newFields[0]?.questionSchema.conditionValue
                            );
                        } else {
                            return (
                                que.title === newFields[0]?.title && que.name !== newFields[0]?.name
                            );
                        }
                    })
                  ? newFields.slice(1).some((field) =>
                        formData.step4.templateFormFields?.find((que) => {
                            const questionSchema = field.questionSchema;
                            if (
                                questionSchema.conditionValue !== undefined &&
                                que.questionSchema.conditionValue !== undefined
                            ) {
                                return (
                                    que.title === field.title &&
                                    que.name !== field.name &&
                                    que.questionSchema.conditionValue ===
                                        questionSchema.conditionValue
                                );
                            } else {
                                return que.title === field.title && que.name !== field.name;
                            }
                        }),
                    )
                  : newFields.some((field) =>
                        formData.step4.templateFormFields?.find((que) => {
                            const questionSchema = field.questionSchema;
                            if (
                                questionSchema.conditionValue !== undefined &&
                                que.questionSchema.conditionValue !== undefined
                            ) {
                                return (
                                    que.title === field.title &&
                                    que.name !== field.name &&
                                    que.questionSchema.conditionValue ===
                                        questionSchema.conditionValue
                                );
                            } else {
                                return que.title === field.title && que.name !== field.name;
                            }
                        }),
                    );
        // Return from the function with an error
        if (similarQuestion) {
            errorSnackbar('This question already exists!');
            return;
        }
        let result;
        if (activeQuote.quoteTemplateId && formData.step4.templateFormId) {
            if (questionDrawer?.formField?.templateFormFieldId) {
                result = findConditionalQuestions(
                    formData.step4.templateFormFields,
                    questionDrawer.formField.templateFormFieldId,
                );
                if (result?.length > 0) {
                    const modifiedResult = result.map((item) => {
                        return {
                            ...item,
                            displayOrder: newFields[0].displayOrder,
                            questionSchema: {
                                ...item.questionSchema,
                                order: newFields[0].displayOrder,
                            },
                            questionValidationSchema: item.questionValidationSchema,
                        };
                    });
                    newFields.push(...modifiedResult);
                }
            }
            const payload = {
                templateFormType: 'IntakeForm',
                headerDescription: formData.step4.intakeFormDescription,
                footerTemplate: formData.step4.textAfterSubmitIntakeForClient,
                templateFormFields: newFields,
            };
            setQuestionDrawerLoading(true);
            try {
                const updateTemplateFormResp = await updateTemplateForm(
                    activeQuote.quoteTemplateId,
                    formData.step4.templateFormId,
                    payload,
                );

                const allNames = updateTemplateFormResp.data.templateFormFields.map(
                    (field) => field.name,
                );

                const updatedResp = formData.step4.templateFormFields.filter(
                    (f) => !allNames.includes(f.name),
                );

                setFormData('step4', {
                    ...formData.step4,
                    templateFormFields: [
                        ...updatedResp,
                        ...updateTemplateFormResp.data.templateFormFields.map((field) => {
                            return {
                                ...field,
                            };
                        }),
                    ],
                });

                successSnackbar(
                    result?.length > 0 ? 'Form field updated successfully' : 'New question added',
                );
                setQuestionDrawerLoading(false);
                setQuestionDrawer({ isOpen: false, tab: '', formField: undefined });
                return;
            } catch (error) {
                console.error(error);
                setQuestionDrawerLoading(false);
                return;
            }
        }

        setFormData('step4', {
            ...formData.step4,
            templateFormFields: [...(formData.step4.templateFormFields ?? []), ...newFields],
        });
        successSnackbar('New question added');
    };

    // Deleting custom question
    const onFieldDelete = async (field: TemplateFormField) => {
        // Enhanced delete logic for conditional questions
        if (field.templateFormFieldId && activeQuote.quoteTemplateId) {
            setIsDeleteFieldLoading(true);
            try {
                const conditions = field?.questionSchema?.conditions;
                // If field has conditions array with length > 0
                if (Array.isArray(conditions) && conditions.length > 0) {
                    // Find all conditional questions
                    const templateFormFields = formData.step4.templateFormFields;
                    const conditionalQuestions = findConditionalQuestions(
                        templateFormFields,
                        field.templateFormFieldId,
                    ) as Array<{ templateFormFieldId: string; name: string }>;
                    // Delete all conditional questions
                    for (const q of conditionalQuestions) {
                        if (q.templateFormFieldId) {
                            await deleteFormField(
                                activeQuote.quoteTemplateId,
                                q.templateFormFieldId,
                            );
                        }
                    }
                    // Delete the parent question itself
                    await deleteFormField(activeQuote.quoteTemplateId, field.templateFormFieldId);
                    // Remove all deleted questions from local state
                    const namesToDelete = [field.name, ...conditionalQuestions.map((q) => q.name)];
                    const updatedStep4 = produce(formData, (draftFormData) => {
                        const currentStep4 = { ...draftFormData.step4 };
                        currentStep4.templateFormFields = currentStep4.templateFormFields?.filter(
                            (f) => !namesToDelete.includes(f.name),
                        );
                        draftFormData.step4 = currentStep4;
                    });
                    setFormData('step4', updatedStep4.step4);
                    setIsDeleteFieldLoading(false);
                    setDeleteAlert({ isOpen: false, field: undefined });
                    successSnackbar(
                        `Field "${field?.title}" and its conditional questions deleted!`,
                    );
                } else {
                    // ...existing code...
                    await deleteFormField(activeQuote.quoteTemplateId, field.templateFormFieldId);
                    // Remove the field from local state and from parent's conditions array (immer draft mutation)
                    const conditionedBy = field.questionSchema.conditionedBy;
                    const updatedStep4 = produce(formData, (draftFormData) => {
                        const currentStep4 = { ...draftFormData.step4 };
                        // If the field is a conditional question, find the parent and remove it from conditions
                        if (field.questionSchema.isConditionalQuestion && conditionedBy) {
                            const parentQuestion = currentStep4.templateFormFields?.find(
                                (f) => f.name === conditionedBy,
                            );
                            if (
                                parentQuestion &&
                                Array.isArray(parentQuestion.questionSchema.conditions)
                            ) {
                                parentQuestion.questionSchema.conditions =
                                    parentQuestion.questionSchema.conditions.filter(
                                        (condition) => condition.nextQuestion?.name !== field.name,
                                    );
                            }
                        }
                        currentStep4.templateFormFields = currentStep4.templateFormFields?.filter(
                            (f) => f.name !== field.name,
                        );
                        draftFormData.step4 = currentStep4;
                    });
                    // If parent needs to be updated, do it here using the updated state (not draft)
                    if (
                        field.questionSchema.isConditionalQuestion &&
                        conditionedBy &&
                        activeQuote.quoteTemplateId &&
                        formData.step4.templateFormId
                    ) {
                        const parentQuestionToUpdate = updatedStep4.step4.templateFormFields?.find(
                            (f) => f.name === conditionedBy,
                        );
                        if (parentQuestionToUpdate?.templateFormFieldId) {
                            await updateFormFields(
                                activeQuote.quoteTemplateId,
                                formData.step4.templateFormId,
                                parentQuestionToUpdate.templateFormFieldId,
                                {
                                    ...parentQuestionToUpdate,
                                    subTitle: parentQuestionToUpdate.subTitle
                                        ? parentQuestionToUpdate.subTitle
                                        : 'NA',
                                },
                            );
                        }
                    }
                    setFormData('step4', updatedStep4.step4);
                    setIsDeleteFieldLoading(false);
                    setDeleteAlert({ isOpen: false, field: undefined }); //Close the delete alert box
                    successSnackbar(`Field "${field?.title}" deleted!`);
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <Box sx={intakeDesignContainerStyle}>
            {isFullScreenLoading && <FullScreenLoader />}
            <Box
                sx={{
                    ...intakeDesignSectionStyle,
                    borderTop: `16px solid ${theme.palette.primary.P_450}`,
                    padding: '16px 32px 32px 32px',
                    marginTop: '32px',
                    gap: '24px',
                }}
            >
                {showTip && (
                    <Alert
                        icon={<InfoSolid />}
                        severity="info"
                        onClose={() => {
                            setShowTip(false);
                        }}
                    >
                        <AlertTitle>Disclaimer</AlertTitle>
                        <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                            Please note the form below is an example of what your future clients
                            will see. To make it complete you have to fill:
                        </Typography>
                        <List
                            sx={{
                                listStyleType: 'disc',
                                pl: 2,
                                ml: 2,
                                '& .MuiListItem-root': {
                                    display: 'list-item',
                                },
                            }}
                        >
                            <ListItem sx={{ padding: 0, margin: 0 }}>
                                <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                    form description, which clients will see at the top of your
                                    form;
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ padding: 0, margin: 0 }}>
                                <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                    ending text, which clients will see after submitting the form;
                                </Typography>
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                            Customize, reorder, or hide questions to tailor the form to your needs.
                        </Typography>
                    </Alert>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '32px',
                        alignItems: 'flex-start',
                    }}
                >
                    <LawFirmLogo lawFirmId={lawFirmId?.toString()} />
                    <Box sx={{ height: '100%', width: '100%' }}>
                        <Typography variant="h1">{formTitle}</Typography>
                        <TextField
                            label="Form description"
                            multiline
                            disabled={!!isDisabled}
                            rows={4}
                            sx={intakeDesignTextAreaStyles}
                            name="intakeFormDescription"
                            value={formData.step4.intakeFormDescription}
                            error={formData.step4.intakeFormDescription?.length > 4000}
                            helperText={
                                formData.step4.intakeFormDescription?.length <= 4000
                                    ? `${formData.step4.intakeFormDescription?.length}/4000`
                                    : 'Exceeded character limit!'
                            }
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                            ) => handleValueChange(event.target.name, event.target.value)}
                        />
                    </Box>
                </Box>
            </Box>
            <CommonTab
                tabs={groupedFields && Object.keys(groupedFields)}
                currentTab={formData.step4.currentIntakeTab}
                setCurrentTab={(tab) =>
                    setFormData('step4', {
                        ...formData.step4,
                        currentIntakeTab: tab,
                    })
                }
                tabComponents={
                    <Button
                        disabled={!!isDisabled}
                        variant="contained"
                        onClick={() =>
                            setQuestionDrawer({
                                isOpen: true,
                                tab: formData.step4.currentIntakeTab.tabLabel,
                                formField: undefined,
                            })
                        }
                        sx={{
                            marginRight: '16px',
                        }}
                    >
                        <AddIcon /> Add Question
                    </Button>
                }
                contents={
                    groupedFields &&
                    Object.entries(groupedFields).map(([group, fields], groupIndex) => (
                        <Box key={groupIndex}>
                            <Typography
                                sx={{
                                    marginLeft: '16px',
                                    fontWeight: 600,
                                    marginBottom: '16px',
                                }}
                                variant="h2"
                            >
                                {group}
                            </Typography>
                            {fields
                                ?.filter((field) => !field.questionSchema.isConditionalQuestion)
                                .sort((a, b) => a.displayOrder - b.displayOrder)
                                ?.map((field, fieldIndex) => (
                                    <Box
                                        key={fieldIndex}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            order: field.displayOrder,
                                        }}
                                    >
                                        <BuilderFormComponent
                                            fieldObj={field}
                                            lawFirmId={lawFirmId as number}
                                            allQuestions={formData.step4.templateFormFields}
                                            formGroupIndex={groupIndex}
                                            isFieldActionEnabled={!isDisabled}
                                            showSelectBoxValue={true}
                                            isCriticalQuestion={field.isCriticalQuestion}
                                            includeFieldAutomationRequired={true}
                                            disabled={true}
                                            isVisible={
                                                field.isVisibleToUser ||
                                                field.isRequiredForFullAutomation
                                            }
                                            isLocked={
                                                isDisabled ||
                                                (isCopy && field.isRequiredForFullAutomation)
                                                    ? true
                                                    : field.isLocked
                                            }
                                            onOrderChange={(order) => {
                                                if (!isDisabled) {
                                                    setNewOrder(order, field);
                                                }
                                            }}
                                            onVisibilityChange={(visibility) => {
                                                setVisibility(visibility, field);
                                            }}
                                            onEditClick={(fieldObj) => onEditInitHandler(fieldObj)}
                                            onDeleteClick={(field) =>
                                                setDeleteAlert({ isOpen: true, field: field })
                                            }
                                        />
                                    </Box>
                                ))}
                        </Box>
                    ))
                }
            />
            <Box sx={{ ...intakeDesignSectionStyle, gap: '0px', marginBottom: '32px' }}>
                <Typography variant="body1">
                    Please enter the ending text that clients will see after submitting the form
                </Typography>
                <TextField
                    placeholder="e.g. “Thank you for submitting the form, our lawyers will contact you in two working days.”"
                    multiline
                    rows={3}
                    disabled={!!isDisabled}
                    sx={intakeDesignTextAreaStyles}
                    name="textAfterSubmitIntakeForClient"
                    value={formData.step4.textAfterSubmitIntakeForClient}
                    error={formData.step4.textAfterSubmitIntakeForClient?.length > 10000}
                    helperText={
                        formData.step4.textAfterSubmitIntakeForClient?.length <= 10000
                            ? `${formData.step4.textAfterSubmitIntakeForClient?.length}/10000`
                            : 'Exceeded character limit!'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleValueChange(event.target.name, event.target.value)
                    }
                />
            </Box>
            <NewQuestionsDrawer
                isOpen={questionDrawer.isOpen}
                onClose={() => setQuestionDrawer({ isOpen: false, tab: '', formField: undefined })}
                onConfirm={handleConfirmAddNewField}
                isLoading={questionDrawerLoading}
                previousQuestionOrder={getLastQuestionOrder(formData.step4.templateFormFields)}
                formGroup={formData.step4.currentIntakeTab.tab + 1}
                formGroupTitle={questionDrawer.tab}
                formField={questionDrawer.formField}
            />
            <AlertDialog
                open={deleteAlert.isOpen}
                handleClose={() => setDeleteAlert({ isOpen: false, field: undefined })}
                title={'Are you sure you want to delete this custom question?'}
                message="This action cannot be undone."
                handleConfirm={() => onFieldDelete(deleteAlert.field)}
                isLoading={isDeleteFieldLoading}
            />
            {formData.step0.isIntakeCreated &&
                formData.step0.intakeFormStatus !== 'Completed' &&
                formData.step0.intakeFormAdded !== 'Manually' &&
                Object.values(groupedFields).some((fields) =>
                    fields?.some(
                        (field) =>
                            field.isRequiredForFullAutomation &&
                            !field.questionSchema.isConditionalQuestion,
                    ),
                ) && (
                    <Dialog
                        onClose={handleDialogClose}
                        open={dialogOpen}
                        PaperProps={{
                            style: {
                                position: 'absolute',
                                top: 0,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                maxWidth: '90%',
                            },
                        }}
                    >
                        <DialogTitle
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >
                                <InfoSolid />
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Required Questions for Quote & Intake (not required in your
                                    selected Intake form):
                                </Typography>
                                <IconButton aria-label="Close" onClick={handleDialogClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            {groupedFields &&
                                Object.values(groupedFields).map((fields, groupIndex) => (
                                    <Box key={groupIndex}>
                                        {fields
                                            ?.filter(
                                                (field) =>
                                                    field.isRequiredForFullAutomation &&
                                                    !field.questionSchema.isConditionalQuestion,
                                            )
                                            .sort((a, b) => a.displayOrder - b.displayOrder)
                                            ?.map((field, fieldIndex) => (
                                                <Typography key={fieldIndex} variant="body1" mb={1}>
                                                    &#8226; {field.title}
                                                </Typography>
                                            ))}
                                    </Box>
                                ))}
                        </DialogContent>
                    </Dialog>
                )}
        </Box>
    );
}
